import { useMemo, useState } from 'react';
import { BlockWrapper, defineBlock, smartProps } from '@smartfire/js-block-engine';
import { Collapse, useMediaQuery } from '@mui/material';
import { MenuMobile } from './MenuMobile';
import { SearchWidget } from './SearchWidget';
import { CountryPanel } from './CountryPanel';
import { SubMenu } from './SubMenu';
import MenuIcon from '../assets/icons/menu';
import SearchIcon from '../assets/icons/search';
import WorldIcon from '../assets/icons/world';
import ShopIcon from '../assets/icons/shop';
import UserIcon from '../assets/icons/user';
import { ReactComponent as Logo } from '../assets/logo.svg';
import { ReactComponent as LogoMobile } from '../assets/logo_mobile.svg';
import { __ } from '../shared/tools';

import style from './style.module.scss';

const HeaderBlock = ({ ...props }) => {
    const isMobile = useMediaQuery(`(max-width:${style.mobileBreakpoint})`);
    return isMobile ? <HeaderMobile {...props} /> : <HeaderDesktop {...props} />;
};

const HeaderDesktop = ({
    mainMenu,
    homeUrl,
    searchQuery,
    myaccountUrl,
    cartUrl,
    languages,
    countries,
    activeLang,
    activeCountry,
}) => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [countryPanelOpen, setCountryPanelOpen] = useState(false);
    const [currentMenuItemId, setCurrentMenuItemId] = useState(0);

    const currentMenuItem = useMemo(() => {
        if (!mainMenu || !mainMenu.length || !currentMenuItemId) return {};
        return mainMenu.find((item) => {
            return item.ID === currentMenuItemId;
        });
    }, [mainMenu, currentMenuItemId]);

    const handleSubmenu = (e, menuId) => {
        e.preventDefault();
        setCurrentMenuItemId((prevMenuId) => (menuId !== prevMenuId ? menuId : 0));
    };

    return (
        <BlockWrapper.Header className={style.header}>
            <div className={style.main}>
                <div className={style.top}>
                    <a href={homeUrl} className={style.logo}>
                        <Logo />
                    </a>
                    <nav className={style.topMenu}>
                        <ul>
                            <li>
                                <button onClick={() => setSearchOpen(!searchOpen)} title={__('Rechercher')}>
                                    <SearchIcon color={style.primary500} />
                                </button>
                            </li>
                            {myaccountUrl && (
                                <li>
                                    <a href={myaccountUrl} title={__('Mon compte')}>
                                        <UserIcon color={style.primary500} />
                                    </a>
                                </li>
                            )}
                            {cartUrl && (
                                <li>
                                    <a href={cartUrl} title={__('Panier')}>
                                        <ShopIcon color={style.primary500} />
                                    </a>
                                </li>
                            )}
                            <li>
                                <button
                                    onClick={() => setCountryPanelOpen(!countryPanelOpen)}
                                    title={__('Changer de pays')}
                                >
                                    <WorldIcon color={style.primary500} />
                                </button>
                            </li>
                        </ul>
                    </nav>
                </div>
                {mainMenu && (
                    <nav className={style.mainMenu}>
                        <ul>
                            {mainMenu.map((menuItem) => {
                                const menuItemProps =
                                    menuItem.children.length > 0
                                        ? { onClick: (event) => handleSubmenu(event, menuItem.ID) }
                                        : { href: menuItem.url, title: menuItem.title };
                                return (
                                    <li key={menuItem.ID}>
                                        <a {...menuItemProps}>{menuItem.title}</a>
                                    </li>
                                );
                            })}
                        </ul>
                    </nav>
                )}
            </div>
            <SearchWidget open={searchOpen} homeUrl={homeUrl} searchQuery={searchQuery} />
            <CountryPanel
                panelOpen={countryPanelOpen}
                setPanelOpen={setCountryPanelOpen}
                languages={languages}
                countries={countries}
                activeLang={activeLang}
                activeCountry={activeCountry}
            />
            {mainMenu &&
                mainMenu.map((menuItem) => {
                    return menuItem.children?.length > 0 ? (
                        <Collapse key={'submenu_' + menuItem.ID} in={currentMenuItem.ID === menuItem.ID}>
                            <SubMenu menuItem={menuItem} onMouseLeave={() => setCurrentMenuItemId(0)} />
                        </Collapse>
                    ) : null;
                })}
        </BlockWrapper.Header>
    );
};

const HeaderMobile = ({
    mainMenuMobile,
    homeUrl,
    searchQuery,
    myaccountUrl,
    cartUrl,
    languages,
    countries,
    activeLang,
    activeCountry,
}) => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [countryPanelOpen, setCountryPanelOpen] = useState(false);
    // const [currentMenuItemId, setCurrentMenuItemId] = useState(0);

    return (
        <BlockWrapper.Header className={style.header}>
            <nav className={style.mobileMenu}>
                <div>
                    <button onClick={() => setMenuOpen((isopen) => !isopen)} title={__('Menu')}>
                        <MenuIcon color={style.primary500} open={menuOpen} />
                    </button>
                    <button onClick={() => setSearchOpen(!searchOpen)} title={__('Rechercher')}>
                        <SearchIcon color={style.primary500} />
                    </button>
                </div>
                <a href={homeUrl} className={style.logo}>
                    <LogoMobile />
                </a>
                <div>
                    {myaccountUrl && (
                        <a href={myaccountUrl} title={__('Mon compte')}>
                            <UserIcon color={style.primary500} />
                        </a>
                    )}
                    {cartUrl && (
                        <a href={cartUrl} title={__('Panier')}>
                            <ShopIcon color={style.primary500} />
                        </a>
                    )}
                    <button onClick={() => setCountryPanelOpen(!countryPanelOpen)} title={__('Changer de pays')}>
                        <WorldIcon color={style.primary500} />
                    </button>
                    <CountryPanel
                        panelOpen={countryPanelOpen}
                        setPanelOpen={setCountryPanelOpen}
                        languages={languages}
                        countries={countries}
                        activeLang={activeLang}
                        activeCountry={activeCountry}
                    />
                </div>
            </nav>
            <SearchWidget open={searchOpen} homeUrl={homeUrl} searchQuery={searchQuery} />
            {mainMenuMobile && mainMenuMobile.length > 0 && (
                <Collapse in={menuOpen}>
                    <MenuMobile items={mainMenuMobile} />
                </Collapse>
            )}
        </BlockWrapper.Header>
    );
};

export default defineBlock({
    Block: HeaderBlock,
    smartProps: {
        languages: smartProps.array.withDefault([]).contentServerSide(),
        activeLang: smartProps.string.withDefault('fr').contentServerSide(),
        homeUrl: smartProps.string.withDefault('').contentServerSide(),
        searchQuery: smartProps.string.withDefault('').contentServerSide(),
        mainMenu: smartProps.array.withDefault([]).contentServerSide(),
        mobileMenu: smartProps.array.withDefault([]).contentServerSide(),
        cartUrl: smartProps.string.withDefault('').contentServerSide(),
        myaccountUrl: smartProps.string.withDefault('').contentServerSide(),
        translations: smartProps.object.withDefault({}).contentServerSide(),
    },
    setup: {
        title: 'Header',
    },
});
